// @mui material components
import { History, PersonAddAlt1, PersonOff } from "@mui/icons-material";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/ApiService";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Swal from "sweetalert2";
import Constants from "utils/Constants";
import Helpers from "utils/Helpers";
import ToastUtils from "utils/ToastUtils";

function UsersActionCell({ id, userName, disabled, refreshTable, accessLevel }) {
  const navigate = useNavigate();
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const { badgeColors } = colors;

  const handleOpenCreditTransactions = () => {
    setCreditDialogOpen(true);
    getCreditTransactions();
  };

  const handleCloseCreditTransactions = () => {
    setCreditDialogOpen(false);
  };

  const getCreditTransactions = async () => {
    const response = await ApiService.getData(
      `/api/v1/credit-transactions?userId=${id}&type=ADMIN_TO_USER&enrich=true`
    );
    if (!response) {
      ToastUtils.error("Αδυναμία εύρεσης συναλλαγών!");
      return;
    }

    const transactions = response.data.data;
    transactions.map((el) => {
      el.createdAt = Helpers.formatDateToNormal(el.createdAt);
    });
    setCreditTransactions(transactions);
  };

  const handleEdit = () => {
    navigate(`/users-edit/${id}`);
  };

  const handleDisable = () => {
    Swal.fire({
      icon: "warning",
      title: "Απενεργοποίηση Χρήστη?",
      text: "Σίγουρα θέλετε να απενεργοποιήσετε το χρήστη? O χρήστης δεν θα μπορεί να συνδεθεί μετά την ενεργοποίηση.",
      showCancelButton: true,
      confirmButtonText: "ΑΠΕΝΕΡΓΟΠΟΙΗΣΗ",
      cancelButtonText: "ΑΚΥΡΟ",
    }).then((result) => {
      if (!result.isConfirmed) return;
      deactivateUser();
    });
  };

  const handleEnable = () => {
    Swal.fire({
      icon: "warning",
      title: "Ενεεργοποίηση Χρήστη?",
      text: "Σίγουρα θέλετε να ενεργοποιήσετε το χρήστη? O χρήστης θα μπορεί να συνδεθεί μετά την ενεργοποίηση.",
      showCancelButton: true,
      confirmButtonText: "ΕΝΕΡΓΟΠΟΙΗΣΗ",
      cancelButtonText: "ΑΚΥΡΟ",
    }).then((result) => {
      if (!result.isConfirmed) return;
      activateUser();
    });
  };

  const deactivateUser = async () => {
    const response = await ApiService.postData(`/api/v1/users/${id}/disable`, {});
    if (!response) {
      ToastUtils.error("Αδυναμία απενεργοποίησης χρήστη!");
      return;
    }
    ToastUtils.success("Επιτυχία απενεργοποίησης χρήστη!");
    refreshTable();
  };

  const activateUser = async () => {
    const response = await ApiService.postData(`/api/v1/users/${id}/enable`, {});
    if (!response) {
      ToastUtils.error("Αδυναμία ενεργοποίησης χρήστη!");
      return;
    }
    ToastUtils.success("Επιτυχία ενεργοποίησης χρήστη!");
    refreshTable();
  };

  const handleTopUp = () => {
    Swal.fire({
      icon: "info",
      title: "Προσθήκη παραστατικών",
      text: "Πληκτρολογήστε τον αριθμό παραστατικών που θέλετε να προσθέσετε στο χρήστη.",
      input: "number",
      showCancelButton: true,
      cancelButtonText: "ΑΚΥΡΟ",
      confirmButtonText: "ΠΡΟΣΘΗΚΗ",
      inputAttributes: {
        autocapitalize: "off",
      },
      preConfirm: async (credits) => {
        const response = await ApiService.postData(`/api/v1/users/${id}/top-up`, {
          credits: Number(credits),
        });

        if (!response) {
          Swal.fire({
            icon: "error",
            title: "Αποτυχία!",
            text: "Αποτυχία προσθήκης παραστατικών!",
          });
          return false;
        }
        return true;
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({
        icon: "success",
        title: "Επιτυχία!",
        text: "Επιτυχής προσθήκη παραστατικών!",
      });
      refreshTable();
    });
  };

  return (
    <SoftBox display="flex" alignItems="center">
      {accessLevel === Constants.ACCESS_LEVELS.MASTER && (
        <SoftBox mx={2}>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{ cursor: "pointer", lineHeight: 0 }}
            onClick={handleTopUp}
          >
            <Tooltip title="Προσθήκη παραστατικών" placement="top">
              <Icon>add</Icon>
            </Tooltip>
          </SoftTypography>
        </SoftBox>
      )}
      <SoftBox mx={2}>
        <SoftTypography
          variant="body1"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0 }}
          onClick={handleEdit}
        >
          <Tooltip title="Επεξεργασία" placement="top">
            <Icon>edit</Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
      <SoftBox mx={2}>
        <SoftTypography
          variant="body1"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0 }}
          onClick={handleOpenCreditTransactions}
        >
          <Tooltip title="Συναλλαγές credits" placement="top">
            <History />
          </Tooltip>
        </SoftTypography>
      </SoftBox>
      {disabled ? (
        <SoftTypography
          variant="body1"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0 }}
          onClick={handleDisable}
        >
          <Tooltip title="Απενεργοποίηση" placement="left">
            <PersonOff />
          </Tooltip>
        </SoftTypography>
      ) : (
        <SoftTypography
          variant="body1"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0 }}
          onClick={handleEnable}
        >
          <Tooltip title="Ενεργοποίηση" placement="left">
            <PersonAddAlt1 />
          </Tooltip>
        </SoftTypography>
      )}
      <Dialog
        open={creditDialogOpen}
        onClose={handleCloseCreditTransactions}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Συναλλαγές Credits - {userName}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ boxShadow: "none" }}>
            <Table>
              <SoftBox component="thead">
                <TableRow>
                  <DataTableHeadCell>ID</DataTableHeadCell>
                  <DataTableHeadCell>CREDITS</DataTableHeadCell>
                  <DataTableHeadCell>ΥΠΟΛΟΙΠΟ</DataTableHeadCell>
                  <DataTableHeadCell>ΧΡΗΣΤΗΣ</DataTableHeadCell>
                  <DataTableHeadCell>ΗΜΕΡΟΜΗΝΙΑ</DataTableHeadCell>
                </TableRow>
              </SoftBox>
              <TableBody>
                {creditTransactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      backgroundColor:
                        transaction.credits > 0
                          ? badgeColors.success.background
                          : transaction.credits < 0
                          ? badgeColors.error.background
                          : "transparent",
                    }}
                  >
                    <DataTableBodyCell>{transaction.id}</DataTableBodyCell>
                    <DataTableBodyCell>
                      <SoftTypography variant="button" fontWeight="bold" color="text">
                        {transaction.credits}
                      </SoftTypography>
                    </DataTableBodyCell>
                    <DataTableBodyCell>{transaction.targetBalanceAfter}</DataTableBodyCell>
                    <DataTableBodyCell>{transaction.actorUserName}</DataTableBodyCell>
                    <DataTableBodyCell>{transaction.createdAt}</DataTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="secondary" onClick={handleCloseCreditTransactions}>
            ΚΛΕΙΣΙΜΟ
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default UsersActionCell;
